import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import {
  Sparkles,
  Play,
  Clapperboard,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  ArrowRight,
  AlertCircle,
  Loader2,
} from 'lucide-react';

// Import the enhanced sample photos used throughout the app
import goldenHourAfter from '@/assets/golden-hour-after.jpg';
import stagingAfter from '@/assets/staging-after.jpg';
import qualityAfter from '@/assets/quality-after.jpg';
import clutterAfter from '@/assets/clutter-after.jpg';
import skyLawnAfter from '@/assets/sky-lawn-after.jpg';
import heroAfter from '@/assets/hero-after.jpg';

const DEMO_COOLDOWN_KEY = 'demo_cinematic_usage';
const MAX_DEMOS = 2;

function checkClientCooldown(): boolean {
  try {
    const raw = localStorage.getItem(DEMO_COOLDOWN_KEY);
    if (!raw) return true;
    return JSON.parse(raw) < MAX_DEMOS;
  } catch { return true; }
}

function recordClientUsage() {
  try {
    const raw = localStorage.getItem(DEMO_COOLDOWN_KEY);
    const count = raw ? JSON.parse(raw) : 0;
    localStorage.setItem(DEMO_COOLDOWN_KEY, JSON.stringify(count + 1));
  } catch {}
}

const samplePhotos = [
  { src: heroAfter,       label: 'Exterior' },
  { src: goldenHourAfter, label: 'Golden Hour' },
  { src: stagingAfter,    label: 'Living Room' },
  { src: qualityAfter,    label: 'Kitchen' },
  { src: clutterAfter,    label: 'Bedroom' },
  { src: skyLawnAfter,    label: 'Garden' },
];

/** Fetch a local asset URL as base64 */
async function assetToBase64(src: string): Promise<string> {
  const res = await fetch(src);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

type DemoState = 'idle' | 'generating' | 'result' | 'error';

export function CinematicVideoDemo({ onGetStarted }: { onGetStarted?: () => void }) {
  const navigate = useNavigate();
  const [photoIndex, setPhotoIndex] = useState(0);
  const [state, setState] = useState<DemoState>('idle');
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [elapsedSecs, setElapsedSecs] = useState(0);

  const selectedPhoto = samplePhotos[photoIndex];

  const prev = () => setPhotoIndex((i) => (i - 1 + samplePhotos.length) % samplePhotos.length);
  const next = () => setPhotoIndex((i) => (i + 1) % samplePhotos.length);

  const handleGenerate = useCallback(async () => {
    if (!checkClientCooldown()) {
      setError('Free demo limit reached. Create an account to generate unlimited videos!');
      setState('error');
      return;
    }

    setState('generating');
    setError('');
    setElapsedSecs(0);

    // Tick elapsed time so user knows it's working
    const ticker = setInterval(() => setElapsedSecs((s) => s + 1), 1000);

    try {
      const base64 = await assetToBase64(selectedPhoto.src);

      const { data, error: fnErr } = await supabase.functions.invoke('demo-cinematic-clip', {
        body: { imageBase64: base64 },
      });

      clearInterval(ticker);

      if (fnErr) throw new Error(fnErr.message || 'Generation failed');
      if (!data?.videoUrl) throw new Error(data?.error || 'No video returned');

      recordClientUsage();
      setVideoUrl(data.videoUrl);
      setState('result');
    } catch (err: unknown) {
      clearInterval(ticker);
      const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(msg);
      setState('error');
    }
  }, [selectedPhoto]);

  const handleReset = () => {
    setVideoUrl(null);
    setError('');
    setElapsedSecs(0);
    setState('idle');
  };

  const handleCta = () => {
    if (onGetStarted) {
      onGetStarted();
    } else {
      navigate('/auth?next=create');
    }
  };

  return (
    <div className="rounded-2xl overflow-hidden border border-border/60 bg-card shadow-elevated">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border/40 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Clapperboard className="w-4 h-4 text-primary" />
          </div>
          <div>
            <p className="text-sm font-bold text-foreground">Live AI Demo</p>
            <p className="text-xs text-muted-foreground">Pick a photo and generate a real cinematic clip</p>
          </div>
        </div>
        <Badge variant="secondary" className="bg-primary/10 text-primary border-0 text-xs">
          <Sparkles className="w-3 h-3 mr-1" /> Free · No sign-up
        </Badge>
      </div>

      <div className="p-6 space-y-5">
        {/* Photo selector */}
        {state !== 'result' && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Choose a sample photo</p>
            <div className="relative rounded-xl overflow-hidden aspect-video bg-muted border border-border/40">
              <img
                src={selectedPhoto.src}
                alt={selectedPhoto.label}
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
              />
              {/* Label */}
              <div className="absolute top-3 left-3">
                <span className="text-xs font-semibold text-white bg-black/50 backdrop-blur-sm px-2.5 py-1 rounded-full">
                  {selectedPhoto.label}
                </span>
              </div>
              {/* Nav arrows */}
              {state === 'idle' && (
                <>
                  <button
                    onClick={prev}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
                    aria-label="Previous photo"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={next}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
                    aria-label="Next photo"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </>
              )}
              {/* Generating overlay */}
              {state === 'generating' && (
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex flex-col items-center justify-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  </div>
                  <div className="text-center">
                    <p className="text-white font-semibold text-sm">Generating AI video clip…</p>
                    <p className="text-white/60 text-xs mt-0.5">
                      {elapsedSecs < 5 ? 'Submitting to AI model…' :
                       elapsedSecs < 20 ? 'AI is generating motion…' :
                       elapsedSecs < 50 ? 'Rendering frames…' :
                       'Almost there…'}
                      {' '}{elapsedSecs}s
                    </p>
                  </div>
                </div>
              )}
            </div>
            {/* Dot indicators */}
            <div className="flex items-center justify-center gap-1.5">
              {samplePhotos.map((_, i) => (
                <button
                  key={i}
                  onClick={() => state === 'idle' && setPhotoIndex(i)}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${i === photoIndex ? 'bg-primary' : 'bg-border hover:bg-muted-foreground'}`}
                  aria-label={`Select photo ${i + 1}`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Result video */}
        {state === 'result' && videoUrl && (
          <div className="space-y-4">
            <div className="rounded-xl overflow-hidden border border-primary/20 shadow-lg bg-black aspect-video">
              <video
                src={videoUrl}
                controls
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                <Play className="w-2.5 h-2.5 text-white fill-white ml-0.5" />
              </div>
              <p className="text-sm font-semibold text-foreground">Real AI-generated motion — not a slideshow</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {state === 'error' && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-3">
            <AlertCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">{error}</p>
              <button onClick={handleReset} className="text-xs text-primary hover:underline mt-1 flex items-center gap-1">
                <RotateCcw className="w-3 h-3" /> Try again
              </button>
            </div>
          </div>
        )}

        {/* Action buttons */}
        {state === 'idle' && (
          <Button variant="hero" className="w-full" size="lg" onClick={handleGenerate}>
            <Sparkles className="w-4 h-4 mr-2" />
            Generate AI Video Clip
          </Button>
        )}

        {state === 'generating' && (
          <Button disabled className="w-full" size="lg">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Generating… {elapsedSecs}s
          </Button>
        )}

        {state === 'result' && (
          <div className="space-y-2">
            <Button variant="hero" className="w-full" size="lg" onClick={handleCta}>
              <Sparkles className="w-4 h-4 mr-2" />
              Create My Listing Video
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button variant="ghost" className="w-full text-muted-foreground" onClick={handleReset}>
              <RotateCcw className="w-3.5 h-3.5 mr-2" />
              Try another photo
            </Button>
          </div>
        )}

        {state === 'idle' && (
          <p className="text-center text-xs text-muted-foreground">
            Uses real fal.ai Pixverse AI · Takes ~30–90 seconds · 2 free demos
          </p>
        )}
      </div>
    </div>
  );
}
